import {useRouteQuery, useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useLocation, useParams, useSearchParams} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import {useMemo} from 'react'

import {repoPullsDashboardContentRoute, repoPullsQueryResolver} from '../routes/repo-pulls-dashboard-content-route'
import {repoPullsToListItems} from '../helpers/repo-pulls-dashboard-proto'

export function useRepoPullsSearch() {
  const location = useLocation()
  const navigate = useNavigate()
  const {owner, repo} = useParams<{owner: string; repo: string}>()
  const [searchParams] = useSearchParams()

  const fetchedQuery = repoPullsQueryResolver({pathname: location.pathname, searchParams})
  const currentPage = Number(searchParams.get('page') || 1)
  const pathname = owner && repo ? `/${owner}/${repo}/pulls` : location.pathname

  function fetchResults({q, page = 1}: {q: string; page?: number}) {
    const nextSearchParams = new URLSearchParams()
    const normalizedQuery = q.trim()
    if (normalizedQuery) nextSearchParams.set('q', normalizedQuery)
    if (page > 1) nextSearchParams.set('page', String(page))
    const milestone = searchParams.get('milestone')
    if (milestone) nextSearchParams.set('milestone', milestone)

    const search = nextSearchParams.toString()
    navigate({pathname, search: search ? `?${search}` : ''})
    window.scrollTo({top: 0})
  }

  return {currentPage, fetchedQuery, fetchResults, owner, repo}
}

export function useRepoPullsDashboardData() {
  const mainQuery = useSuspenseRouteQuery(repoPullsDashboardContentRoute, 'mainQuery')
  const deferredQuery = useRouteQuery(repoPullsDashboardContentRoute, 'deferred')
  const mainResults = mainQuery.data.results
  const deferredResults = deferredQuery.data?.error ? undefined : deferredQuery.data?.results
  const pulls = useMemo(() => repoPullsToListItems(mainResults, deferredResults), [deferredResults, mainResults])
  const stateCounts =
    mainQuery.data.openCount === undefined || mainQuery.data.closedCount === undefined
      ? undefined
      : {open: mainQuery.data.openCount, closed: mainQuery.data.closedCount}

  return {
    error: mainQuery.data.error,
    isDeferredLoading: mainResults.length > 0 && deferredQuery.isPending,
    pageInfo: {
      currentPage: mainQuery.data?.currentPage ?? 1,
      totalPages: mainQuery.data?.totalPages ?? 0,
      totalCount: mainQuery.data?.totalCount ?? 0,
    },
    pulls,
    stateCounts,
  }
}
